import { getSalesId, getSalesValue } from '../hooks/useSalesApiResource';

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asQuantity = (value, fallback = 1) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

const asApiQuantity = (value) => {
  const quantity = asQuantity(value, 1);
  return quantity === 0 ? null : quantity;
};

export const PART_QUANTITY_MODE_FIXED = 'fixed';
export const PART_QUANTITY_MODE_PUMP_ROWS = 'pumpRows';

export const isPumpQuantitySourceRule = (rule, pumpQuantityFieldId) => (
  asNumber(pumpQuantityFieldId) > 0
  && asNumber(rule?.fieldId) === asNumber(pumpQuantityFieldId)
  && rule?.requiredValueId === null
  && rule?.minValue === null
  && rule?.maxValue === null
);

export const normalizePartMatchRule = (row = {}) => {
  return {
    id: asNumber(getSalesId(row)),
    partId: asNumber(getSalesValue(row, 'partId', 'PartId')),
    stationId: asNumber(getSalesValue(row, 'masterStationId', 'MasterStationId')),
    ruleGroupId: asNumber(getSalesValue(row, 'ruleGroupId', 'RuleGroupId'), 1),
    quantity: asQuantity(getSalesValue(row, 'quantity', 'Quantity'), 0),
    requiredValueId: asNullableNumber(getSalesValue(
      row,
      'requiredValueId',
      'RequiredValueId',
      'requiredFieldValueId',
      'RequiredFieldValueId'
    )),
    fieldId: asNullableNumber(getSalesValue(row, 'fieldId', 'FieldId', 'numericFieldId', 'NumericFieldId')),
    minValue: asNullableNumber(getSalesValue(row, 'minValue', 'MinValue', 'minMatch', 'MinMatch')),
    maxValue: asNullableNumber(getSalesValue(row, 'maxValue', 'MaxValue', 'maxMatch', 'MaxMatch')),
  };
};

export const getPartMatchRuleGroups = (
  rules = [],
  partId,
  stationId = null,
  { pumpQuantityFieldId = 0 } = {}
) => {
  const numericStationId = asNullableNumber(stationId);
  const groups = new Map();
  rules
    .filter((rule) => (
      rule.partId === asNumber(partId)
      && (numericStationId === null || rule.stationId === numericStationId)
    ))
    .forEach((rule) => {
      const current = groups.get(rule.ruleGroupId) || [];
      current.push(rule);
      groups.set(rule.ruleGroupId, current);
    });
  return [...groups.entries()]
    .sort(([left], [right]) => left - right)
    .map(([ruleGroupId, conditions]) => {
      const sortedConditions = [...conditions].sort((left, right) => left.id - right.id);
      const quantitySourceRule = sortedConditions.find((rule) => (
        isPumpQuantitySourceRule(rule, pumpQuantityFieldId)
      ));
      return {
        ruleGroupId,
        quantity: conditions[0]?.quantity ?? 0,
        quantityMode: quantitySourceRule ? PART_QUANTITY_MODE_PUMP_ROWS : PART_QUANTITY_MODE_FIXED,
        quantitySourceRuleId: quantitySourceRule?.id || 0,
        conditions: quantitySourceRule
          ? sortedConditions.filter((rule) => rule.id !== quantitySourceRule.id)
          : sortedConditions,
        allConditions: sortedConditions,
      };
    });
};

export const getNextPartRuleGroupId = (groups = []) => (
  Math.max(0, ...groups.map((group) => asNumber(group.ruleGroupId))) + 1
);

export const buildPartMatchRuleCopyDraft = (
  rules = [],
  partId,
  sourceStationId,
  targetStationId,
  startingRuleGroupId = null,
  pumpQuantityFieldId = 0
) => {
  const sourceGroups = getPartMatchRuleGroups(rules, partId, sourceStationId, { pumpQuantityFieldId });
  let nextRuleGroupId = asNumber(startingRuleGroupId) || getNextPartRuleGroupId(rules);
  return {
    partId: asNumber(partId),
    stationId: asNumber(targetStationId),
    sourceStationId: asNumber(sourceStationId),
    groups: sourceGroups.map((group) => ({
      ruleGroupId: nextRuleGroupId++,
      quantity: group.quantity,
      quantityMode: group.quantityMode,
      quantitySourceRuleId: 0,
      conditions: group.conditions.map((rule) => ({
        id: 0,
        type: rule.requiredValueId ? 'value' : rule.fieldId ? 'numeric' : 'default',
        requiredValueId: rule.requiredValueId || 0,
        fieldId: rule.fieldId || 0,
        minValue: rule.minValue ?? '',
        maxValue: rule.maxValue ?? '',
      })),
    })),
    pumpQuantityFieldId: asNumber(pumpQuantityFieldId),
  };
};

export const filterPartAutoSelectCategories = (
  categoryRows = [],
  selectedCategoryId = 'all',
  searchTerm = ''
) => {
  const needle = searchTerm.trim().toLowerCase();
  const categoryId = selectedCategoryId === 'all' ? null : asNumber(selectedCategoryId);

  return categoryRows
    .filter((category) => categoryId === null || category.id === categoryId)
    .map((category) => {
      const categoryMatches = [category.code, category.name, category.description]
        .some((value) => String(value || '').toLowerCase().includes(needle));
      const activeParts = !needle || categoryMatches
        ? category.activeParts
        : category.activeParts.filter((part) => [part.partNumber, part.name]
          .some((value) => String(value || '').toLowerCase().includes(needle)));
      return { ...category, activeParts };
    })
    .filter((category) => category.activeParts.length > 0);
};

export const getStationAssignedFieldValues = (fieldValues = [], valueRules = [], stationId = 0) => {
  const numericStationId = asNumber(stationId);
  const assignedValueIds = new Set(
    valueRules
      .filter((rule) => rule.parentStationId === numericStationId && !rule.isDisabled)
      .map((rule) => rule.fieldValueId)
  );
  return fieldValues.filter((value) => assignedValueIds.has(value.id));
};

export const getStationAutoPickFieldValues = (
  fieldValues = [],
  valueRules = [],
  stationId = 0,
  stationPartRules = []
) => {
  const availableValueIds = new Set([
    ...getStationAssignedFieldValues(fieldValues, valueRules, stationId).map((value) => value.id),
    ...stationPartRules.map((rule) => asNumber(rule.requiredValueId)).filter((valueId) => valueId > 0),
  ]);
  return fieldValues.filter((value) => availableValueIds.has(value.id));
};

const getSelectedValueIdSet = (selections = {}) => new Set(
  Object.values(selections)
    .map((value) => asNumber(value))
    .filter((value) => value > 0)
);

const numericConditionMatches = (rule, inputs = {}) => {
  const rawValue = inputs[rule.fieldId];
  if (rawValue === '' || rawValue === null || rawValue === undefined) return false;
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) return false;
  if (rule.minValue !== null && numericValue < Number(rule.minValue)) return false;
  if (rule.maxValue !== null && numericValue > Number(rule.maxValue)) return false;
  return true;
};

const partRuleConditionMatches = (rule, selectedValueIds, inputs) => {
  if (rule.requiredValueId) return selectedValueIds.has(rule.requiredValueId);
  if (rule.fieldId) return numericConditionMatches(rule, inputs);
  return true;
};

const getOptionNumberSortParts = (optionNumber) => {
  const normalized = String(optionNumber || '').trim();
  const separatorIndex = normalized.indexOf('-');
  const prefixText = normalized.slice(0, 3);
  const suffixText = separatorIndex >= 0 ? normalized.slice(separatorIndex + 1) : '';

  return {
    normalized,
    prefix: /^\d{3}$/.test(prefixText) ? Number(prefixText) : Number.MAX_SAFE_INTEGER,
    suffix: /^\d+$/.test(suffixText) ? Number(suffixText) : Number.MAX_SAFE_INTEGER,
  };
};

const compareOptionNumbers = (leftOptionNumber, rightOptionNumber) => {
  const left = getOptionNumberSortParts(leftOptionNumber);
  const right = getOptionNumberSortParts(rightOptionNumber);

  return left.prefix - right.prefix
    || left.suffix - right.suffix
    || left.normalized.localeCompare(right.normalized, undefined, { numeric: true });
};

export const buildPartAutoPickPreviewModel = ({
  parts = [],
  categories = [],
  assignments = [],
  rules = [],
  fields = [],
} = {}, stationId = 0, selections = {}, inputs = {}, pumpRows = []) => {
  if (pumpRows.some(row => row.isValid === false)) {
    return { rows: [], selectedPartCount: 0, extendedPriceTotal: 0, error: 'Correct the pump configuration quantities or field errors before generating Auto Pick.' };
  }
  const numericStationId = asNumber(stationId);
  const pumpContexts = pumpRows.map((pumpRow) => ({
    selectedValueIds: getSelectedValueIdSet({ ...selections, ...(pumpRow?.selections || {}) }),
    inputs: { ...inputs, ...(pumpRow?.inputs || {}) },
  }));
  // Match a complete AND group within one row, not across unrelated pumps.
  const fixedContexts = pumpContexts.length ? pumpContexts : [{
    selectedValueIds: getSelectedValueIdSet(selections), inputs,
  }];
  const pumpQuantityFieldId = fields.find((field) => (
    String(field.fieldName || '').trim().toLowerCase() === 'quantity'
  ))?.id || 0;
  const assignedPartIds = new Set(assignments
    .filter((assignment) => assignment.stationId === numericStationId)
    .map((assignment) => assignment.partId));
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const rulesByPart = new Map();

  rules.forEach((rule) => {
    if (!assignedPartIds.has(rule.partId) || rule.stationId !== numericStationId) return;
    const partRules = rulesByPart.get(rule.partId) || [];
    partRules.push(rule);
    rulesByPart.set(rule.partId, partRules);
  });

  const rows = parts
    .filter((part) => assignedPartIds.has(part.id))
    .flatMap((part) => {
      const groups = getPartMatchRuleGroups(
        rulesByPart.get(part.id) || [],
        part.id,
        numericStationId,
        { pumpQuantityFieldId }
      );
      const matchingFixedGroups = groups
        .filter((group) => group.quantityMode === PART_QUANTITY_MODE_FIXED)
        .filter((group) => fixedContexts.some((context) => group.conditions.every((rule) => (
          partRuleConditionMatches(rule, context.selectedValueIds, context.inputs)
        ))));
      const pumpContributions = groups
        .filter((group) => group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS)
        .flatMap((group) => pumpContexts.flatMap((context) => {
          const matches = group.conditions.every((rule) => (
            partRuleConditionMatches(rule, context.selectedValueIds, context.inputs)
          ));
          if (!matches) return [];
          const pumpQuantity = asNumber(context.inputs[pumpQuantityFieldId]);
          const multiplier = asNumber(group.quantity);
          return [{
            ruleGroupId: group.ruleGroupId,
            quantity: pumpQuantity * multiplier,
          }];
        }));
      const fixedGroup = matchingFixedGroups[0];
      if (!fixedGroup && pumpContributions.length === 0) return [];

      const calculatedQuantity = (fixedGroup ? asNumber(fixedGroup.quantity) : 0)
        + pumpContributions.reduce((total, contribution) => total + contribution.quantity, 0);
      const quantity = calculatedQuantity > 0 ? calculatedQuantity : null;
      const listPrice = asNumber(part.listPrice);
      const category = categoryById.get(part.categoryId);
      return [{
        partId: part.id,
        partNumber: part.partNumber,
        partName: part.name,
        categoryId: part.categoryId,
        categoryCode: category?.code || '',
        categoryName: category?.name || 'Uncategorized',
        categoryDisplayOrder: category?.displayOrder ?? Number.MAX_SAFE_INTEGER,
        partSortOrder: part.sortOrder ?? Number.MAX_SAFE_INTEGER,
        quantity,
        listPrice,
        extendedPrice: listPrice * (quantity || 0),
        matchedRuleGroupIds: [
          ...(fixedGroup ? [fixedGroup.ruleGroupId] : []),
          ...new Set(pumpContributions.map((contribution) => contribution.ruleGroupId)),
        ],
      }];
    })
    .sort((left, right) => (
      compareOptionNumbers(left.partNumber, right.partNumber)
      || left.partName.localeCompare(right.partName)
    ));

  return {
    rows,
    selectedPartCount: rows.length,
    extendedPriceTotal: rows.reduce((total, row) => total + row.extendedPrice, 0),
  };
};

export const validatePartMatchRuleDraft = (draft = {}) => {
  const groups = draft.groups || [];
  const groupIds = new Set();

  for (const group of groups) {
    const groupId = asNumber(group.ruleGroupId);
    if (!groupId) return 'Every rule group requires a positive Rule Group ID.';
    if (groupIds.has(groupId)) return `Rule Group ID ${groupId} is duplicated.`;
    groupIds.add(groupId);
    const quantity = group.quantity === null || group.quantity === undefined ? 1 : Number(group.quantity);
    if (group.quantity === '' || !Number.isInteger(quantity) || quantity < 0) {
      return `${group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? 'Per-pump multiplier' : 'Quantity'} must be a whole number of 0 or more in rule group ${groupId}.`;
    }
    if (
      group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS
      && asNumber(draft.pumpQuantityFieldId) <= 0
    ) {
      return `The Pump Configuration Quantity field is required for pump-derived quantity in rule group ${groupId}.`;
    }
    if (!group.conditions?.length && group.quantityMode !== PART_QUANTITY_MODE_PUMP_ROWS) {
      return `Rule group ${groupId} requires at least one condition.`;
    }
    if (group.conditions.some((condition) => condition.type === 'default') && group.conditions.length > 1) {
      return `Always selected must be the only condition in rule group ${groupId}.`;
    }

    for (const condition of group.conditions) {
      if (condition.type === 'default') {
        continue;
      } else if (condition.type === 'value') {
        if (!asNumber(condition.requiredValueId)) return `Choose a required value in rule group ${groupId}.`;
      } else if (condition.type === 'numeric') {
        if (!asNumber(condition.fieldId)) return `Choose a numeric field in rule group ${groupId}.`;
        const minValue = asNullableNumber(condition.minValue);
        const maxValue = asNullableNumber(condition.maxValue);
        if (minValue !== null && maxValue !== null && minValue > maxValue) {
          return `Minimum cannot exceed maximum in rule group ${groupId}.`;
        }
      } else {
        return `Choose a condition type in rule group ${groupId}.`;
      }
    }
  }
  return '';
};

export const buildPartMatchRulePayload = (partId, ruleGroupId, condition, fieldNames = 'api', stationId = null) => {
  const isValue = condition.type === 'value';
  const isNumeric = condition.type === 'numeric';
  const isPumpQuantity = condition.type === 'pumpQuantity';
  const requiredValueId = isValue ? asNumber(condition.requiredValueId) : null;
  const fieldId = isNumeric || isPumpQuantity ? asNumber(condition.fieldId) : null;
  const payload = {
    ...(condition.id ? { Id: asNumber(condition.id) } : {}),
    PartId: asNumber(partId),
    ...(asNumber(stationId) > 0 ? { MasterStationId: asNumber(stationId) } : {}),
    RuleGroupId: asNumber(ruleGroupId),
    Quantity: asApiQuantity(condition.quantity),
    MinValue: isNumeric ? asNullableNumber(condition.minValue) : null,
    MaxValue: isNumeric ? asNullableNumber(condition.maxValue) : null,
  };

  if (fieldNames === 'api') {
    payload.RequiredFieldValueId = requiredValueId;
    payload.NumericFieldId = fieldId;
  } else {
    payload.RequiredValueId = requiredValueId;
    payload.FieldId = fieldId;
  }
  return payload;
};

const normalizedComparableRule = (row) => ({
  partId: asNumber(row.partId),
  stationId: asNumber(row.stationId),
  ruleGroupId: asNumber(row.ruleGroupId),
  quantity: asQuantity(row.quantity, 0),
  requiredValueId: asNullableNumber(row.requiredValueId),
  fieldId: asNullableNumber(row.fieldId),
  minValue: asNullableNumber(row.minValue),
  maxValue: asNullableNumber(row.maxValue),
});

const payloadToComparableRule = (payload) => normalizedComparableRule({
  partId: payload.PartId,
  stationId: payload.MasterStationId,
  ruleGroupId: payload.RuleGroupId,
  quantity: payload.Quantity,
  requiredValueId: payload.RequiredValueId ?? payload.RequiredFieldValueId,
  fieldId: payload.FieldId ?? payload.NumericFieldId,
  minValue: payload.MinValue ?? payload.MinMatch,
  maxValue: payload.MaxValue ?? payload.MaxMatch,
});

export const buildPartMatchRuleOperations = (draft, existingRows = [], fieldNames = 'api') => {
  const nextIds = new Set();
  const writes = [];

  (draft.groups || []).forEach((group) => {
    const editableConditions = group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS
      ? (group.conditions || []).filter((condition) => condition.type !== 'default')
      : (group.conditions || []);
    const conditions = [
      ...editableConditions,
      ...(group.quantityMode === PART_QUANTITY_MODE_PUMP_ROWS ? [{
        id: group.quantitySourceRuleId || 0,
        type: 'pumpQuantity',
        fieldId: draft.pumpQuantityFieldId,
      }] : []),
    ];
    conditions.forEach((condition) => {
      const payload = buildPartMatchRulePayload(
        draft.partId,
        group.ruleGroupId,
        { ...condition, quantity: group.quantity },
        fieldNames,
        draft.stationId
      );
      const existing = condition.id
        ? existingRows.find((row) => row.id === asNumber(condition.id))
        : null;
      if (existing) {
        nextIds.add(existing.id);
        if (JSON.stringify(normalizedComparableRule(existing)) !== JSON.stringify(payloadToComparableRule(payload))) {
          writes.push({ method: 'PUT', payload });
        }
      } else {
        writes.push({ method: 'POST', payload });
      }
    });
  });

  const deletes = existingRows
    .filter((row) => !nextIds.has(row.id))
    .map((row) => ({ method: 'DELETE', payload: { Id: row.id } }));
  return [...writes, ...deletes];
};

export const findMatchingPartRule = (rules = [], payload = {}) => {
  const target = payloadToComparableRule(payload);
  return rules.find((rule) => (
    JSON.stringify(normalizedComparableRule(rule)) === JSON.stringify(target)
  ));
};
